import { ApiError, apiRequest } from "./api";

/**
 * Wire shape for GET /api/verification/path — the SILL-537 M5
 * (vf-entrypoint-unification, D1) shared path decision. This mirrors the
 * backend's `verificationflow.Path` JSON tags exactly: `link_mode` is what
 * Plaid Link mode a caller should mint ("" means an existing item already
 * covers this buyer — no new Link session needed), `steps` is the ordered
 * server-driven step list, and `reason` is the buyer-facing explanation the
 * caller is expected to render verbatim (never re-derive).
 */
export type VerificationLinkMode = "" | "initial" | "transactions";

export type VerificationPath = {
  link_mode: VerificationLinkMode;
  steps: string[];
  reason: string;
  unplanned?: boolean;
};

type VerificationPathResponse = {
  path: VerificationPath;
};

type TokenResolver = (options?: {
  skipCache?: boolean;
}) => Promise<string | null>;

/**
 * Fetches the caller's CURRENT shared verification path. Every entry point
 * (funnel, Settings, Passport rows) calls this — never a local mode
 * heuristic — so the same buyer sees the same link_mode regardless of
 * which door they walked through (scenario 1694, entry parity).
 *
 * Returns `null` on a 404 (VERIFICATION_FLOW_ENABLED kill-switch off, or
 * the route genuinely absent) rather than throwing — this is the FE half
 * of the vf-kill-switch contract: callers fall back to their pre-M5
 * hardcoded mode when the endpoint isn't there, no coordinated deploy
 * required. Any OTHER failure (network, 5xx, auth) still throws, since
 * those are not "feature is off," they're "something is wrong."
 */
export async function getVerificationPath(
  getToken: TokenResolver,
): Promise<VerificationPath | null> {
  const token = (await getToken({ skipCache: true })) ?? undefined;
  try {
    const response = await apiRequest<VerificationPathResponse>(
      "/api/verification/path",
      { token },
    );
    return response.path;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Wire shape for GET /api/verification/field-capabilities — task 1678.
 * Mirrors the backend's `verificationflow.FieldCapability` JSON tags. The
 * server owns which affordances a passport input can actually honour; this
 * app renders that answer and never decides it locally.
 *
 * `field` matches ProfileRowId's literals exactly (the backend picked those
 * values deliberately) so no translation table exists on either side.
 *
 * The `*_reason` strings explain a false capability in buyer-facing prose
 * and are rendered verbatim — never re-derived, and never present when the
 * matching capability is true.
 */
export type VerificationFieldCapability = {
  field: string;
  supports_bank: boolean;
  bank_reason?: string;
  supports_document: boolean;
  document_reason?: string;
};

type VerificationFieldCapabilitiesResponse = {
  fields: VerificationFieldCapability[];
};

/**
 * Fetches the per-field verification capability table.
 *
 * Returns `null` on ANY failure — including a 404 — rather than throwing.
 * That differs from getVerificationPath, which rethrows non-404s, and the
 * difference is deliberate: a path read feeds a connect flow the user just
 * initiated, so a 5xx there is worth surfacing. This read only decides
 * whether to PAINT an affordance, and callers treat `null` as "offer
 * nothing" (see useVerificationFieldCapabilities). Failing closed on a
 * render decision is strictly safer than throwing inside a passport row —
 * an unhandled rejection there would take down the section, and offering an
 * upload button we cannot honour is the very defect this table fixes.
 *
 * Unlike /api/verification/path, this route is NOT behind
 * VERIFICATION_FLOW_ENABLED, so a 404 here means something is genuinely
 * wrong (bad deploy, routing) rather than "the feature is off".
 */
export async function getVerificationFieldCapabilities(
  getToken: TokenResolver,
): Promise<VerificationFieldCapability[] | null> {
  try {
    // Plain getToken, not `{skipCache: true}`. Its sibling getVerificationPath
    // skips the cache because it feeds a connect flow; this route carries no
    // MFA gate and returns a table identical for every caller, so a forced
    // re-mint is a round-trip for nothing.
    const token = (await getToken()) ?? undefined;
    const response = await apiRequest<VerificationFieldCapabilitiesResponse>(
      "/api/verification/field-capabilities",
      { token },
    );
    return response.fields ?? null;
  } catch {
    return null;
  }
}
